<template>
    <!-- PAGE HEADER -->
    <div class="flex justify-start items-start px-5 xl:px-0 mb-2">
        <!-- Left: Title -->
        <div class="w-full">
            <h1
                class="text-2xl md:text-3xl text-gray-800 font-bold text-center md:text-left"
            >
                Manage Your Connections
            </h1>
        </div>
    </div>
    <!-- END PAGE HEADER -->
    <div class="flex flex-col justify-start items-start px-5 xl:px-0">
        <div
            class="flex justify-between flex-col md:flex-row gap-4 md:gap-0 items-center"
        >
            <div class="md:w-2/3 w-full">
                <p
                    class="text-sm font-normal text-slate-500 text-center md:text-left"
                >
                    Automatically sync your rewards data by adding authorized
                    connections to financial institutions to your My10x account.
                    Removing a connection will delete it along with all
                    associated data from your My10x account.
                </p>
            </div>
            <a
                href="#"
                class="h-10 flex p-2.5 px-4 justify-center items-center gap-2 rounded bg-[var(--Primary-10x-Orange,#FD421D)] shadow-md text-white focus:outline-none focus:ring-2 focus:ring-slate-300 focus:ring-offset-0 whitespace-nowrap transition-colors font-medium text-sm max-w-[166px] hover:bg-orange-500 active:bg-orange-500"
                @click.prevent="handleOpenConnectModal()"
            >
                <Icon icon="plus" />
                Add connection
            </a>
        </div>
        <div class="w-full flex justify-center sm:justify-start items-start">
            <div
                class="rounded flex justify-center items-center gap-2 pt-3 cursor-pointer w-auto mt-4 md:mt-0 mb-4 md:mb-0"
                @click="setShowSecurityModal(true)"
            >
                <Icon icon="security" svgClass="h-5 w-5" />
                <p class="text-sm font-medium font-['Inter'] leading-tight">
                    How we keep your data safe
                </p>
            </div>
        </div>
        <div class="w-full md:mt-12">
            <div class="grid grid-cols-1 gap-x-4 gap-y-2 mb-5">
                <EmptyState
                    v-if="
                        !loadingMembers &&
                        Object.keys(membersByUser).length === 0
                    "
                    :title="'No connections yet'"
                    :description="'Add your first connection to automatically sync rewards data.'"
                    icon="bank"
                >
                    <template #action>
                        <button
                            class="px-4 py-2.5 bg-white rounded shadow text-sm font-medium border border-slate-200 text-slate-800 hover:bg-slate-50 transition-colors active:bg-slate-200 focus:outline-slate-200 focus:border-slate-200 hover:border-slate-300 active:border-slate-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                            @click.prevent="handleOpenConnectModal()"
                        >
                            <Icon icon="plus" />
                            Add connection
                        </button>
                    </template>
                </EmptyState>
                <!-- Single user members -->
                <div v-if="Object.keys(membersByUser).length === 1">
                    <div
                        class="grid grid-cols-1 items-start gap-x-4 gap-y-2 mb-10"
                    >
                        <div v-for="member in members" :key="member.guid">
                            <ConnectionCard :connection="member" />
                        </div>
                    </div>
                </div>

                <!-- Display members grouped by user -->
                <div v-if="Object.keys(membersByUser).length > 1">
                    <div
                        v-for="(userGroup, userGuid) in membersByUser"
                        :key="userGuid"
                    >
                        <h4 class="text-xl font-bold mb-5">
                            {{
                                userGroup.user
                                    ? userGroup.user.name + "'s Connections"
                                    : "Unknown User's Connections"
                            }}
                        </h4>
                        <div
                            class="grid grid-cols-1 items-start gap-x-4 gap-y-2 mb-10"
                        >
                            <div
                                v-for="member in userGroup.members"
                                :key="member.guid"
                            >
                                <ConnectionCard :connection="member" />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
    <!-- END PAGE HEADER -->

    <SecurityModal
        :isOpen="showSecurityModal"
        @close="setShowSecurityModal(false)"
    />

    <ChallengesModal
        :member="member"
        :show="showChallengeModal"
        @close="setShowChallengeModal(false, {})"
        @success="getMembers"
    />

    <ConnectModal
        :wallet="wallet"
        :showAddCardModal="showConnectModal"
        :finishedSyncAccount="finishedSyncAccount"
        :isFixUnresolved="isFixUnresolved"
        :setHowToAddCard="howToAddCard"
        :cards="[]"
        :users="filteredUsers"
        :banks="banks"
        :step="connectStep"
        :connectType="connectType"
        :member="member"
        @close="handleConnectModalClose"
        @addedCard="handleConnectSuccess"
    />
    <div class="fixed inset-0" v-if="loadingMembers">
        <LoadingState :loading="loadingMembers" size="large" noBackground />
    </div>
</template>

<script>
import { mapState, mapActions } from 'pinia';
import { useMembersStore } from '@/store/members';
import Icon from '@/components/ui/icon/icon.vue';
import EmptyState from '@/components/ui/empty-state/empty-state.vue';
import ConnectionCard from '@/components/members/connection-card.vue';
import ChallengesModal from '@/components/wallet/modals/challenges.vue';
import vClickOutside from 'click-outside-vue3';
import SecurityModal from '@/components/security/security-modal.vue';
import ConnectModal from '@/components/wallet/modals/connect.vue';
import LoadingState from '@/components/ui/loading-state/loading-state.vue';

export default {
    name: 'MemberConnections',
    props: [],
    components: {
        Icon,
        EmptyState,
        ConnectionCard,
        ChallengesModal,
        SecurityModal,
        ConnectModal,
        LoadingState,
    },
    directives: {
        ClickOutside: vClickOutside.directive,
    },
    computed: {
        ...mapState(useMembersStore, [
            'walletId',
            'members',
            'sharedUsers',
            'banks',
            'loadingMembers',
            'showConnectModal',
            'showChallengeModal',
            'member',
            'connectType',
            'wallet',
            'users',
            'membersByUser',
        ]),
        filteredUsers() {
            return this.users.filter((v) => {
                return v.show;
            });
        },
    },
    data: () => ({
        errors: [],
        submittingAnswer: false,
        widgetUrl: {},
        mxWidget: null,
        showSecurityModal: false,
        finishedSyncAccount: false,
        isFixUnresolved: false,
        connectStep: 1,
        howToAddCard: '',
        // members: [],
    }),

    methods: {
        ...mapActions(useMembersStore, [
            'getMembers',
            'setShowConnectModal',
            'setShowChallengeModal',
        ]),
        handleOpenConnectModal() {
            this.howToAddCard = 'connect_to_bank';
            this.setShowConnectModal(true, {}, '');
        },
        setShowSecurityModal(value) {
            this.showSecurityModal = value;
        },
        handleConnectModalClose() {
            this.setShowConnectModal(false, {}, '');
            this.connectStep = 1;
        },
        handleConnectSuccess() {
            this.getMembers();
            this.handleConnectModalClose();
        },
        handleConnectionError() {
            this.$notify(
                {
                    group: 'error',
                    title: 'Connection Error',
                    text: 'There was an error connecting to your bank. Please try again or contact support.',
                },
                10000
            );
            this.getMembers();
            this.connectStep = 1;
        },
    },

    created() {},
    mounted() {
        this.getMembers();
    },
};
</script>
<style lang="scss" scoped>
.loading-wallet {
    position: fixed;
    background-color: rgba(000, 000, 000, 0.75);
    border-radius: 4px;
    padding: 16px 24px;
    text-align: center;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 2000;
}
.fadeOut-enter-active,
.fadeOut-leave-active {
    transition: opacity 0.8s;
}
.fadeOut-enter,
.fadeOut-leave-to {
    opacity: 0;
}
</style>
